import { Calendar, Clock, Target, TrendingUp, Zap } from "lucide-react";
import React, { useEffect, useState } from "react";
import { SectionHeader } from "../index";
import { useTranslation } from "../../contexts/LanguageContext";
import apiService, { API_ENDPOINTS } from "../../shared/apiService";
import { cn } from "../../utils/cn";
import ContentRecommendationsSkeleton from "./skeletons/ContentRecommendationsSkeleton";

interface OptimalTime {
  hour: number;
  avg_engagement: number;
  posts_count: number;
}

interface EffectiveCTA {
  cta: string;
  conversion_rate: number;
  total_clicks: number;
  total_conversions: number;
  posts_count: number;
}

interface ContentRecommendationsProps {
  days: number;
}

/**
 * Aba "Recomendações de Conteúdo" — mescla o que antes eram as abas
 * "Horários Ótimos" (OptimalTimes.tsx) e "CTAs de Conversão"
 * (EffectiveCTAs.tsx). Os dois componentes originais consumiam a MESMA
 * requisição (`LEARNING_DASHBOARD.DASHBOARD?days=`), só liam chaves
 * diferentes do payload (`optimal_times` vs `effective_ctas`) — mesclar
 * elimina a requisição duplicada quando o usuário navegava entre as duas
 * abas, sem perder nenhuma informação. Cada seção mantém seu próprio
 * SectionHeader por serem, semanticamente, duas seções distintas dentro
 * da mesma aba.
 */
const ContentRecommendations: React.FC<ContentRecommendationsProps> = ({
  days,
}) => {
  const { t } = useTranslation();
  const [times, setTimes] = useState<OptimalTime[] | null>(null);
  const [ctas, setCtas] = useState<EffectiveCTA[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    apiService
      .get(`${API_ENDPOINTS.LEARNING_DASHBOARD.DASHBOARD}?days=${days}`)
      .then((result: any) => {
        if (!isMounted) return;
        const data = result?.success && result?.data ? result.data : result;
        setTimes(data?.optimal_times || []);
        setCtas(data?.effective_ctas || []);
      })
      .catch(() => {
        if (isMounted) {
          setTimes([]);
          setCtas([]);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [days]);

  const formatHour = (hour: number) => `${hour.toString().padStart(2, "0")}:00`;
  const formatPercentage = (num: number | undefined | null) => {
    if (num === undefined || num === null || Number.isNaN(num)) return "0.0%";
    return `${(num * 100).toFixed(1)}%`;
  };
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) return <ContentRecommendationsSkeleton />;

  const bestHour =
    times && times.length > 0
      ? [...times].sort((a, b) => {
          const aVal =
            typeof a.avg_engagement === "number" &&
            !Number.isNaN(a.avg_engagement)
              ? a.avg_engagement
              : 0;
          const bVal =
            typeof b.avg_engagement === "number" &&
            !Number.isNaN(b.avg_engagement)
              ? b.avg_engagement
              : 0;
          return bVal - aVal;
        })[0]
      : null;

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Horários Ótimos */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <SectionHeader
          icon={<Clock className="h-5 w-5" />}
          title={t("Horários Ótimos")}
          subtitle={t(
            "Janelas de maior engajamento baseadas no histórico da audiência",
          )}
          iconBgClass="bg-purple-600"
          action={
            bestHour && (
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5 self-center">
                <Zap className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                <div className="flex flex-col leading-none">
                  <span className="text-[8px] font-black uppercase tracking-widest text-purple-500">
                    {t("Pico")}
                  </span>
                  <span className="text-sm font-black text-gray-900 tabular-nums">
                    {formatHour(bestHour.hour)}
                  </span>
                </div>
              </div>
            )
          }
        />

        {!times || times.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center bg-purple-50/20 rounded-xl border border-dashed border-purple-200">
            <Clock className="h-8 w-8 text-purple-300 mb-2" />
            <p className="text-xs font-semibold text-gray-700">
              {t("Nenhuma janela de alta performance detectada neste ciclo.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[...times]
              .sort((a, b) => a.hour - b.hour)
              .map((time) => {
                const isBest = bestHour?.hour === time.hour;
                const isMorning = time.hour >= 5 && time.hour < 12;
                const isAfternoon = time.hour >= 12 && time.hour < 18;
                const engagementVal =
                  typeof time.avg_engagement === "number" &&
                  !Number.isNaN(time.avg_engagement)
                    ? time.avg_engagement
                    : 0;
                const postsCount = time.posts_count ?? 0;

                return (
                  <div
                    key={time.hour}
                    className={cn(
                      "relative bg-white rounded-xl border p-3 shadow-xs flex flex-col justify-between min-h-[104px] overflow-hidden",
                      isBest ? "border-purple-400" : "border-purple-200",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-0 left-0 right-0 h-[3px]",
                        isBest ? "bg-purple-600" : "bg-purple-100",
                      )}
                    />
                    {isBest && (
                      <span className="absolute top-2 right-2 text-[8px] font-black uppercase tracking-widest text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5">
                        {t("Top")}
                      </span>
                    )}
                    <div className="flex items-start justify-between pt-0.5">
                      <div className="flex flex-col min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          {postsCount} {t("interações")}
                        </p>
                        <h3 className="text-xl font-black tracking-tight leading-none tabular-nums text-gray-900 mt-1">
                          {formatHour(time.hour)}
                        </h3>
                      </div>
                      <div
                        className={cn(
                          "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                          isBest
                            ? "bg-purple-600 text-white"
                            : "bg-purple-50 text-purple-400",
                        )}
                      >
                        {isMorning ? (
                          <Zap className="h-4 w-4" />
                        ) : isAfternoon ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <Target className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all duration-500",
                            isBest ? "bg-purple-600" : "bg-purple-300",
                          )}
                          style={{
                            width: `${Math.min(engagementVal * 1000, 100)}%`,
                          }}
                        />
                      </div>
                      <p
                        className={cn(
                          "text-[10px] font-black tabular-nums mt-1",
                          isBest ? "text-purple-700" : "text-gray-400",
                        )}
                      >
                        {formatPercentage(engagementVal)}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* CTAs de Conversão */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <SectionHeader
          icon={<Target className="h-5 w-5" />}
          title={t("CTAs de Conversão")}
          subtitle={t(
            "Chamadas para ação com maior índice de clique e resposta",
          )}
          iconBgClass="bg-emerald-600"
        />

        {!ctas || ctas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center bg-purple-50/20 rounded-xl border border-dashed border-purple-200">
            <Target className="h-8 w-8 text-purple-300 mb-2" />
            <p className="text-xs font-semibold text-gray-700">
              {t("Sem dados de conversão para as CTAs analisadas.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ctas.map((cta, index) => {
              const conversionRate =
                typeof cta.conversion_rate === "number" &&
                !Number.isNaN(cta.conversion_rate)
                  ? cta.conversion_rate
                  : 0;
              const postsCount = cta.posts_count ?? 0;
              const totalClicks = cta.total_clicks ?? 0;
              const ctaText = cta.cta || "—";

              return (
                <div
                  key={index}
                  className="flex flex-col p-3 border border-purple-200 bg-white rounded-xl hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">
                          {t("Alta Eficácia")}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-gray-900 leading-tight truncate">
                        &ldquo;{ctaText}&rdquo;
                      </p>
                    </div>
                    <div className="bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-100 flex flex-col items-center shrink-0">
                      <div className="text-base font-black text-emerald-600 tracking-tight leading-none">
                        {formatPercentage(conversionRate)}
                      </div>
                      <div className="text-[8px] font-black uppercase tracking-widest text-emerald-600/60">
                        {t("conversão")}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    <div className="h-1 w-full bg-gray-50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${conversionRate * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {postsCount}{" "}
                        {t("amostras")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3" /> {formatNumber(totalClicks)}{" "}
                        {t("cliques")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentRecommendations;
